/**
 * Task Runner Component
 * 
 * Wraps prioritization tools for participant task completion.
 * Handles:
 * - Loading the task and its items
 * - Displaying the appropriate tool
 * - Capturing and submitting responses
 * - Showing completion confirmation
 */

import { useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Users,
} from 'lucide-react';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { usePrioritizationTaskStore } from '../../../stores/prioritization/prioritizationTaskStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { ToolItemsProvider } from '../../../contexts/ToolItemsContext';
import type { ToolItem, ToolResultItem } from '../../prioritization/ToolWrapper';
import { 
  PRIORITIZATION_TOOLS, 
  TASK_CATEGORY_LABELS,
  type TaskResponseData,
  type TaskCategory,
} from '../../../types/prioritization/prioritizationTask';
import { isCatchballProposalWorkbenchPath } from '../../../lib/taskNavigation';

// Import category-specific task components
import { ValueEntryTask } from './ValueEntryTask';
import { CriteriaSelectionTask } from './CriteriaSelectionTask';
import { WeightAssignmentTask } from './WeightAssignmentTask';
import { ItemReviewTask } from './ItemReviewTask';

// Import tool components for dynamic rendering
import StackRanking from '../../../pages/stack-ranking/StackRanking';
import DotVoting from '../../../pages/dot-voting/DotVoting';
import BuyAFeature from '../../../pages/buy-a-feature/BuyAFeature';
import MoscowMethod from '../../../pages/moscow-method/MoscowMethod';
import RiceScoring from '../../../pages/rice-scoring/RiceScoring';
import WeightedSmallestJobFirst from '../../../pages/wsjf/WeightedSmallestJobFirst';
import ImpactEffortMatrix from '../../../pages/impact-effort-matrix/ImpactEffortMatrix';
import PairwiseRanking from '../../../pages/pairwise-ranking/PairwiseRanking';
import AHP from '../../../pages/ahp/AHP';
import { SUBMIT_MY_RANKING_LABEL } from '@/lib/sessions/vocabulary';

// ============================================================================
// Types
// ============================================================================

type SubmissionState = 'idle' | 'submitting' | 'submitted' | 'error';

// ============================================================================
// Tool ID normalizer
// Session tools use underscore IDs (dot_voting, pairwise_ranking) while the
// task runner uses hyphenated IDs (dot-voting, pairwise-comparison). Map them.
// ============================================================================

const SESSION_TO_TASK_TOOL_ID: Record<string, string> = {
  pairwise_ranking: 'pairwise-comparison',
  pairwise: 'pairwise-comparison',
  stack_ranking: 'stack-ranking',
  dot_voting: 'dot-voting',
  impact_effort: 'impact-effort',
  buy_a_feature: 'buy-a-feature',
  rice: 'rice-scoring',
  moscow: 'moscow-method',
  weighted_scoring: 'rice-scoring',
};

function normalizeToolId(toolId: string): string {
  return SESSION_TO_TASK_TOOL_ID[toolId] ?? toolId.replace(/_/g, '-');
}

// ============================================================================
// Component
// ============================================================================

export function TaskRunner() {
  const { taskId } = useParams<{ taskId: string }>();

  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const [, setResponseData] = useState<TaskResponseData | null>(null);
  const [, setToolResults] = useState<ToolResultItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Get current user
  const userId = useUserStore(state => state.userId);
  const userName = useUserStore(state => state.userName);
  
  // Get task
  const allTasks = usePrioritizationTaskStore(state => state.tasks);
  const submitResponse = usePrioritizationTaskStore(state => state.submitResponse);
  
  const task = useMemo(() => 
    allTasks.find(t => t.id === taskId),
    [allTasks, taskId]
  );
  
  // Check if user has already responded
  const existingResponse = useMemo(() => 
    task?.responses.find(r => r.participantId === userId),
    [task, userId]
  );
  
  // Get cycle info
  const cycles = useDegapProcessStore(state => state.cycles);
  const cycle = useMemo(() => 
    task ? cycles.find(c => c.id === task.cycleId) : undefined,
    [cycles, task]
  );
  
  // Get items for the task
  const allItems = useItemsStore(state => state.items);
  const taskItems = useMemo(() => {
    if (!task) return [];
    return allItems.filter(c => task.itemIds.includes(c.id));
  }, [allItems, task]);
  
  // Calculate deadline info
  const isOverdue = useMemo(() => {
    if (!task?.closesAt) return false;
    return new Date(task.closesAt) < new Date();
  }, [task]);
  
  const formatDeadline = (date?: Date) => {
    if (!date) return 'No deadline';
    const d = new Date(date);
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };
  
  // Handle tool completion — called by ToolWrapper's onCompleteResults.
  // Immediately submits so the single "Submit My Ranking" button in the header
  // is the only thing the participant needs to click.
  const handleToolComplete = useCallback(async (results: ToolResultItem[]) => {
    if (!task) return;

    // Keep state for display purposes (e.g. re-renders)
    setToolResults(results);
    const sorted = [...results].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
    const data: TaskResponseData = {
      type: 'stack-ranking',
      ranking: sorted.map(r => r.id),
    };
    setResponseData(data);

    setSubmissionState('submitting');
    setError(null);

    try {
      // 1. Record in the task store (marks task responded for this participant)
      submitResponse({
        taskId: task.id,
        participantId: userId,
        data,
      });

      // 2. If this task is linked to a session, also write to tool_session_responses
      //    so the facilitator's async panel shows live progress.
      if (task.sessionId) {
        try {
          const { getSafeSupabaseClient } = await import('../../../lib/supabaseSync');
          const client = getSafeSupabaseClient();
          if (client) {
            // Query by session_id only — mode column may be null on older rows
            const { data: ts } = await client
              .from('tool_sessions')
              .select('id')
              .eq('session_id', task.sessionId)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (ts) {
              const { useToolSessionStore } = await import('../../../stores/prioritization/toolSessionStore');
              const { getCurrentWorkspaceId } = await import('../../../lib/supabaseSync');
              await useToolSessionStore.getState().submitParticipantResponse({
                toolSessionId: (ts as { id: string }).id,
                workspaceId: getCurrentWorkspaceId() ?? '',
                participantId: userId ?? '',
                participantName: userName ?? '',
                results,
              });
            }
          }
        } catch {
          // Non-fatal: task response is already recorded above
        }
      }

      setSubmissionState('submitted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit response');
      setSubmissionState('error');
    }
  }, [task, userId, userName, submitResponse]);
  
  // Normalize the tool ID so session underscore IDs (dot_voting) map to task hyphen IDs (dot-voting)
  const normalizedToolId = useMemo(() => task ? normalizeToolId(task.toolId) : '', [task]);

  // Get tool info
  const toolInfo = useMemo(() => 
    task ? PRIORITIZATION_TOOLS.find(t => t.id === normalizedToolId) : undefined,
    [task, normalizedToolId]
  );

  // Determine task category
  const category: TaskCategory = task?.category || 'prioritization';
  
  // Handle completion of category-specific tasks
  const handleCategoryComplete = useCallback(() => {
    setSubmissionState('submitted');
  }, []);
  
  // Render the appropriate task UI based on category
  const renderTaskUI = () => {
    if (!task) return null;
    
    // Non-prioritization categories have their own complete UI
    if (category === 'value_entry') {
      return (
        <ValueEntryTask
          task={task}
          userId={userId}
          userName={userName}
          onComplete={handleCategoryComplete}
        />
      );
    }
    
    if (category === 'criteria_selection') {
      return (
        <CriteriaSelectionTask
          task={task}
          userId={userId}
          userName={userName}
          onComplete={handleCategoryComplete}
        />
      );
    }
    
    if (category === 'weight_assignment') {
      return (
        <WeightAssignmentTask
          task={task}
          userId={userId}
          userName={userName}
          onComplete={handleCategoryComplete}
        />
      );
    }
    
    if (category === 'item_review') {
      return (
        <ItemReviewTask
          task={task}
          userId={userId}
          userName={userName}
          onComplete={handleCategoryComplete}
        />
      );
    }
    
    // Prioritization category - use the tool-based rendering
    return renderTool();
  };
  
  // Convert task items to the ToolItem shape the context expects
  const toolContextItems = useMemo<ToolItem[]>(() =>
    taskItems.map(item => ({
      id: item.id,
      name: (item as { name?: string; title?: string }).name ?? (item as { name?: string; title?: string }).title ?? item.id,
      description: (item as { description?: string }).description,
      sourceId: item.id,
      sourceType: 'item' as const,
    })),
    [taskItems]
  );

  // Render the appropriate prioritization tool wrapped in ToolItemsProvider
  // so ToolWrapper auto-injects items (inputMode=preloaded) and skips the
  // "Add items to get started" zero state.
  const renderTool = () => {
    if (!task || !toolInfo) return null;

    // Props used by ToolWrapper / ToolHostProps
    const hostProps = {
      displayMode: 'page' as const,
      hostContext: 'task' as const,
      onCompleteResults: handleToolComplete,
      completeActionLabel: SUBMIT_MY_RANKING_LABEL,
    };

    const toolElement = (() => {
      switch (normalizedToolId) {
        case 'stack-ranking': return <StackRanking {...hostProps} />;
        case 'dot-voting': return <DotVoting {...hostProps} />;
        case 'buy-a-feature': return <BuyAFeature {...hostProps} />;
        case 'moscow-method': return <MoscowMethod {...hostProps} />;
        case 'rice-scoring': return <RiceScoring {...hostProps} />;
        case 'wsjf': return <WeightedSmallestJobFirst {...hostProps} />;
        case 'impact-effort': return <ImpactEffortMatrix {...hostProps} />;
        case 'pairwise-comparison': return <PairwiseRanking {...hostProps} />;
        case 'ahp': return <AHP {...hostProps} />;
        default:
          return (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-[var(--mustard)]" />
              <p className="text-[var(--text-sec)]">Tool "{task.toolId}" is not yet supported in task mode.</p>
            </div>
          );
      }
    })();

    return (
      <ToolItemsProvider
        items={toolContextItems}
        hostContext="task"
        situationalContext={{
          inputMode: 'preloaded',
          completion: {
            label: SUBMIT_MY_RANKING_LABEL,
            destinationHint: 'Records your response for this session',
          },
          cycleId: task.cycleId ?? undefined,
        }}
      >
        {toolElement}
      </ToolItemsProvider>
    );
  };
  
  // Error state - task not found
  if (!task) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-[var(--mustard)]" />
        <h1 className="text-xl font-bold text-[var(--text-pri)] mb-2">Task Not Found</h1>
        <p className="text-[var(--text-sec)] mb-6">
          The task you're looking for doesn't exist or you don't have access to it.
        </p>
        <Link
          to="/prioritization/my-work"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to My work
        </Link>
      </div>
    );
  }
  
  // Success state - already submitted
  if (submissionState === 'submitted' || existingResponse) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <div className="bg-[var(--green-dim)] p-4 rounded-full inline-flex mb-6">
          <CheckCircle className="h-12 w-12 text-[var(--green)]" />
        </div>
        <h1 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Response Submitted!</h1>
        <p className="text-[var(--text-sec)] mb-6">
          Thank you for completing your prioritization. Your input has been recorded.
        </p>
        
        {/* Response Summary */}
        <div className="bg-[var(--surface)] p-6 mb-6 text-left">
          <h3 className="font-medium text-[var(--text-pri)] mb-3">Your Response Summary</h3>
          <div className="space-y-2 text-sm text-[var(--text-sec)]">
            <p><strong>Task:</strong> {task.name}</p>
            {task.category === 'value_entry' ? (
              <>
                <p><strong>Value:</strong> {task.attributeName ? task.attributeName.charAt(0).toUpperCase() + task.attributeName.slice(1) : 'Values'}</p>
                <p><strong>Items:</strong> {task.itemIds.length}</p>
                {(task.toolConfig as { ask?: boolean } | undefined)?.ask && (
                  <p>Each value is now proposed on its item, for someone who approves values to accept.</p>
                )}
              </>
            ) : (
              <>
                <p><strong>Tool:</strong> {toolInfo?.name || task.toolId}</p>
                <p><strong>Items Prioritized:</strong> {task.itemIds.length}</p>
              </>
            )}
            <p><strong>Submitted:</strong> {
              existingResponse?.submittedAt 
                ? new Date(existingResponse.submittedAt).toLocaleString()
                : 'Just now'
            }</p>
          </div>
        </div>
        
        <div className="flex justify-center gap-4">
          <Link
            to="/prioritization/my-work"
            className="btn btn-primary"
          >
            Back to My work
          </Link>
          {cycle && (
            <Link
              to={`/prioritization/cycles/${cycle.id}`}
              className="btn btn-secondary"
            >
              View Cycle
            </Link>
          )}
        </div>
      </div>
    );
  }
  
  // Task expired state - deadline has passed (check before status check)
  if (isOverdue && !existingResponse) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <div className="bg-[var(--coral-dim)] p-4 rounded-full inline-flex mb-6">
          <Clock className="h-12 w-12 text-[var(--coral)]" />
        </div>
        <h1 className="text-xl font-bold text-[var(--text-pri)] mb-2">Task Expired</h1>
        <p className="text-[var(--text-sec)] mb-4">
          The deadline for this task has passed.
        </p>
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 mb-6 inline-block">
          <p className="text-sm text-[var(--coral)]">
            <strong>Deadline was:</strong> {formatDeadline(task.closesAt)}
          </p>
        </div>
        <p className="text-sm text-[var(--text-ter)] mb-6">
          If you believe this is an error, please contact your facilitator.
        </p>
        <div className="flex justify-center gap-4">
          <Link
            to="/prioritization/my-work"
            className="btn btn-primary"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to My work
          </Link>
          {cycle && (
            <Link
              to={`/prioritization/cycles/${cycle.id}`}
              className="btn btn-secondary"
            >
              View Cycle
            </Link>
          )}
        </div>
      </div>
    );
  }
  
  // Task closed state (status not open)
  if (task.status !== 'open') {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-[var(--mustard)]" />
        <h1 className="text-xl font-bold text-[var(--text-pri)] mb-2">Task Closed</h1>
        <p className="text-[var(--text-sec)] mb-6">
          This task is no longer accepting responses.
        </p>
        <Link
          to="/prioritization/my-work"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to My work
        </Link>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/prioritization/my-work"
            className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-pri)]">{task.name}</h1>
            <p className="text-[var(--text-ter)]">
              {cycle?.name || 'Prioritization Task'} • {
                category === 'prioritization' 
                  ? (toolInfo?.name || task.toolId)
                  : TASK_CATEGORY_LABELS[category]
              }
            </p>
          </div>
        </div>
        
        {/* Deadline Badge */}
        <div className={`px-4 py-2  text-sm font-medium ${
          isOverdue 
            ? 'bg-[var(--coral-dim)] text-[var(--coral)]' 
            : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
        }`}>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {task.closesAt 
              ? formatDeadline(task.closesAt)
              : 'No deadline'
            }
          </div>
        </div>
      </div>
      
      {/* Task Info — skip internal routing paths (e.g. catchball workbench stored in description) */}
      {task.description && !isCatchballProposalWorkbenchPath(task) && (
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
          <p className="text-[var(--text-sec)]">{task.description}</p>
        </div>
      )}
      
      {/* Stats Bar */}
      <div className="flex items-center gap-6 text-sm text-[var(--text-ter)]">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-4 w-4" />
          {task.itemIds.length} items to prioritize
        </span>
        <span className="inline-flex items-center gap-1">
          <Users className="h-4 w-4" />
          {task.responses.length} / {task.participantIds.length} responses
        </span>
      </div>
      
      {/* Task UI Container */}
      <div className="bg-[var(--bg-raised)] border shadow-sm p-6">
        {renderTaskUI()}
      </div>
      
      {/* Error banner - shown if submission fails after tool completion */}
      {error && (
        <div className="p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
          {error} — please try again or contact your facilitator.
        </div>
      )}
    </div>
  );
}

export default TaskRunner;
